<script lang="ts">
	import { Button, Icon, Text } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import {
		ENERGY_PRICE_CTA_TEXT_COLOR,
		energyPriceColorSchemes,
		type ColorSchemeKey,
	} from '../../utils/colorSchemes';
	import VerivoxAttribution from '../../shared/VerivoxAttribution.svelte';
	import VerivoxLogoPlate from '../../shared/VerivoxLogoPlate.svelte';
	import { dismissVerivoxSurface, type VerivoxDismissSurface } from '../../utils/verivoxDismissals';
	import type { VerivoxBannerLayout } from '../verivox-banner-layout';

	/**
	 * Shared Verivox setup promo chrome.
	 * Prefer the placement wrappers (Notifications / Widget / Inline) at call sites.
	 */
	export let layout: VerivoxBannerLayout = 'notifications';
	export let onCtaClick: () => void;
	export let dismissable = false;
	export let dismissSurface: VerivoxDismissSurface = 'notifications';
	export let onDismiss: (() => void) | undefined;

	$: isInline = layout === 'inline';
	$: isWidget = layout === 'widget';
	$: isNotifications = layout === 'notifications';

	let energyPriceScheme: ColorSchemeKey = 'scheme1';
	let isHidden = false;

	onMount(() => {
		energyPriceScheme = energyPriceColorSchemes.getCurrentScheme();
	});

	const handleDismiss = async () => {
		isHidden = true;
		await dismissVerivoxSurface(dismissSurface, 'setup');
		onDismiss?.();
	};
</script>

{#if !isHidden}
	<div
		class="verivox-banner-outer"
		class:verivox-banner-outer--flush={!isNotifications}
		class:verivox-banner-outer--inline={isInline}
		class:verivox-banner-outer--widget={isWidget}
		class:verivox-banner-outer--notifications={isNotifications}
	>
		<div
			class="verivox-banner"
			class:verivox-banner--inline={isInline}
			class:verivox-banner--widget={isWidget}
			class:verivox-banner--notifications={isNotifications}
			style="background: {energyPriceColorSchemes.schemes[
				energyPriceScheme
			]}; --verivox-cta-text: {ENERGY_PRICE_CTA_TEXT_COLOR}"
		>
			<span class="verivox-banner__circle verivox-banner__circle--tr-lg" aria-hidden="true"></span>
			<span class="verivox-banner__circle verivox-banner__circle--tr-sm" aria-hidden="true"></span>

			{#if !isInline}
				<div class="verivox-banner__logo">
					<VerivoxLogoPlate compact={isWidget} />
				</div>
			{/if}

			{#if dismissable}
				<button
					type="button"
					class="verivox-banner__dismiss reset-button-styles-base"
					aria-label={$format('button_dismiss', { default: 'Dismiss' })}
					on:click={handleDismiss}
				>
					<Icon icon="fal fa-times" size="s" />
				</button>
			{/if}

			<div class="verivox-banner__body">
				{#if isInline}
					<div class="verivox-banner__logo-inline">
						<VerivoxLogoPlate compact />
					</div>
				{/if}

				<div class="verivox-banner__main">
					<div class="verivox-banner__heading">
						<Icon icon="fas fa-bolt" size="s" class="verivox-banner__icon" />
						<Text
							styleType={isInline ? 'body-extra-small' : 'body-small'}
							class="verivox-banner__title text-bold justify-content-start"
						>
							{$format('energy_price_setup_prompt_title', {
								default: 'Günstige Stromtarife entdecken',
								locale: 'de',
							})}
						</Text>
					</div>

					{#if !isInline}
						<Text styleType="body-small" class="verivox-banner__message">
							{$format('energy_price_setup_prompt_message', {
								default: 'Vergleichen Sie verfügbare Stromtarife in Ihrer Region',
								locale: 'de',
							})}
						</Text>
					{/if}
				</div>

				<div class="verivox-banner__bottom">
					<div class="verivox-banner__actions">
						<Button
							class="verivox-banner__cta"
							size={isInline ? 's' : 'm'}
							variant="secondary"
							on:click={onCtaClick}
						>
							{$format('button_get_started', {
								default: 'Loslegen',
								locale: 'de',
							})}
						</Button>
						{#if dismissable && isNotifications}
							<Button
								class="verivox-banner__cancel"
								size="m"
								variant="secondary"
								on:click={handleDismiss}
							>
								{$format('button_cancel', {
									default: 'Abbrechen',
									locale: 'de',
								})}
							</Button>
						{/if}
					</div>
					{#if isWidget || isNotifications}
						<div class="verivox-banner__footer">
							<VerivoxAttribution />
						</div>
					{/if}
				</div>
			</div>
		</div>
	</div>
{/if}

<style>
	.verivox-banner-outer {
		padding: var(--spacing-s, 8px);
		height: 100%;
		display: flex;
		flex-direction: column;
		box-sizing: border-box;
	}

	.verivox-banner-outer--flush {
		padding: 0;
	}

	.verivox-banner {
		position: relative;
		overflow: hidden;
		border-radius: 12px;
		isolation: isolate;
		min-height: 0;
		height: 100%;
		flex: 1;
		display: flex;
		flex-direction: column;
		box-sizing: border-box;
	}

	.verivox-banner--notifications {
		max-height: none;
	}

	.verivox-banner--widget {
		max-height: 180px;
	}

	.verivox-banner--inline {
		border-radius: 10px;
		max-height: 120px;
	}

	.verivox-banner__circle {
		position: absolute;
		border-radius: 50%;
		pointer-events: none;
		z-index: 0;
	}

	.verivox-banner__circle--tr-lg {
		top: -48px;
		right: -39px;
		width: 110px;
		height: 110px;
		background: rgba(255, 196, 128, 0.4);
	}

	.verivox-banner__circle--tr-sm {
		top: -18px;
		right: -20px;
		width: 64px;
		height: 64px;
		background: rgba(255, 220, 170, 0.28);
	}

	.verivox-banner--inline .verivox-banner__circle--tr-lg {
		top: -40px;
		right: -32px;
		width: 80px;
		height: 80px;
	}

	.verivox-banner--inline .verivox-banner__circle--tr-sm {
		top: -14px;
		right: -16px;
		width: 48px;
		height: 48px;
	}

	.verivox-banner__logo {
		position: absolute;
		top: 10px;
		left: 10px;
		z-index: 2;
	}

	.verivox-banner--widget .verivox-banner__logo {
		top: 10px;
		left: 12px;
	}

	/* Inline: logo lives in-flow so it cannot overlap the title. */
	.verivox-banner__logo-inline {
		flex-shrink: 0;
		padding-right: 28px;
	}

	.verivox-banner__logo-inline :global(.verivox-logo-plate--compact) {
		padding: 4px 8px;
		border-radius: 7px;
	}

	.verivox-banner__logo-inline :global(.verivox-logo-plate--compact .verivox-logo-plate__img) {
		height: 16px;
		max-width: 80px;
	}

	.verivox-banner__dismiss {
		position: absolute;
		top: 8px;
		right: 8px;
		z-index: 3;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 28px;
		height: 28px;
		border-radius: 6px;
		color: #ffffff;
		cursor: pointer;
		opacity: 0.9;
		transition:
			opacity 0.15s ease,
			background-color 0.15s ease;
	}

	.verivox-banner--inline .verivox-banner__dismiss {
		top: 6px;
		right: 6px;
		width: 22px;
		height: 22px;
		border-radius: 5px;
	}

	.verivox-banner__dismiss:hover {
		opacity: 1;
		background-color: rgba(255, 255, 255, 0.12);
	}

	.verivox-banner__body {
		position: relative;
		z-index: 1;
		display: flex;
		flex-direction: column;
		flex: 1;
		height: 100%;
		min-height: 0;
		gap: 8px;
		padding: 48px 14px 12px;
		box-sizing: border-box;
	}

	.verivox-banner--widget .verivox-banner__body {
		padding: 46px 14px 40px;
		gap: 6px;
	}

	.verivox-banner--notifications .verivox-banner__body {
		padding: 52px 14px 14px;
		gap: 10px;
	}

	/*
	 * Inline (~120px): stacked in-flow logo → title → CTA.
	 * Keep bottom padding light so the CTA isn’t crushed; gallery dots sit over empty space.
	 */
	.verivox-banner--inline .verivox-banner__body {
		padding: 8px 10px 10px;
		gap: 6px;
		justify-content: space-between;
	}

	.verivox-banner__main {
		display: flex;
		flex-direction: column;
		gap: 6px;
		min-width: 0;
	}

	.verivox-banner--inline .verivox-banner__main {
		gap: 0;
		flex: 1 1 auto;
		justify-content: center;
		min-height: 0;
	}

	.verivox-banner__heading {
		display: flex;
		align-items: flex-start;
		gap: 8px;
		padding-right: 28px;
		min-width: 0;
	}

	.verivox-banner--inline .verivox-banner__heading {
		gap: 6px;
		padding-right: 4px;
		align-items: center;
	}

	.verivox-banner__bottom {
		margin-top: auto;
		display: flex;
		flex-direction: column;
		gap: 6px;
		flex-shrink: 0;
	}

	.verivox-banner--inline .verivox-banner__bottom {
		margin-top: 0;
		gap: 0;
	}

	.verivox-banner--widget .verivox-banner__bottom {
		flex-direction: row;
		align-items: flex-end;
		justify-content: space-between;
		gap: 8px;
	}

	.verivox-banner__footer {
		display: flex;
		align-items: flex-end;
		justify-content: flex-end;
	}

	.verivox-banner--widget .verivox-banner__footer {
		flex-shrink: 0;
	}

	.verivox-banner__actions {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
	}

	.verivox-banner--inline .verivox-banner__actions {
		gap: 6px;
	}

	.verivox-banner--inline :global(.verivox-banner__cta) {
		min-height: 28px !important;
		padding: 4px 12px !important;
		font-size: 12px !important;
		border-radius: 7px !important;
	}

	:global(.verivox-banner__icon) {
		color: #ffffff !important;
		flex-shrink: 0;
		margin-top: 1px;
	}

	.verivox-banner--inline :global(.verivox-banner__icon) {
		margin-top: 0;
	}

	:global(.verivox-banner__title) {
		color: #ffffff !important;
		line-height: 1.3;
		min-width: 0;
	}

	.verivox-banner--inline :global(.verivox-banner__title) {
		line-height: 1.25;
	}

	:global(.verivox-banner__message) {
		color: rgba(255, 255, 255, 0.92) !important;
		line-height: 1.35;
	}

	.verivox-banner--widget :global(.verivox-banner__message) {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	:global(.verivox-banner__cta) {
		background: rgba(255, 255, 255, 0.95) !important;
		color: var(--verivox-cta-text, #e64600) !important;
		border: none !important;
		border-radius: 8px !important;
		font-weight: 600 !important;
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15) !important;
	}

	:global(.verivox-banner__cta:hover) {
		background: #ffffff !important;
		transform: translateY(-1px);
		box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2) !important;
	}

	:global(.verivox-banner__cancel) {
		background: transparent !important;
		color: #ffffff !important;
		border: 1px solid rgba(255, 255, 255, 0.55) !important;
		border-radius: 8px !important;
		font-weight: 600 !important;
		box-shadow: none !important;
	}

	:global(.verivox-banner__cancel:hover) {
		background: rgba(255, 255, 255, 0.12) !important;
		border-color: rgba(255, 255, 255, 0.8) !important;
	}

	.verivox-banner__footer :global(.attribution-footer) {
		margin-top: 0;
		flex-shrink: 0;
	}
</style>
